import { matchPath } from "react-router-dom";

export type AttentionType = "input_required" | "completed";

export interface AttentionNotification {
  eventKey: string;
  projectId: string;
  projectName: string | null;
  type: AttentionType;
  phase: string | null;
  title: string;
  message: string | null;
  occurredAt: string | null;
}

// Wire events from GET /notifications/stream (`event: notification`)
export type AttentionStreamEvent =
  | ({ type: AttentionType } & Omit<AttentionNotification, "type">)
  | { type: "input_resolved"; projectId: string; eventKey: string }
  | { type: "inputs_reset"; projectId: string; runId: string }
  | { type: "cleared" };

export const INPUT_TITLE = "Joylo is waiting for your input";
export const COMPLETED_TITLE = "Your workflow has completed successfully";
// A finished wake-up rides on the "completed" type, told apart by phase, so ack/supersede/bell behave like any completion
export const APP_READY_PHASE = "app_ready";
export const APP_READY_TITLE = "Your app is awake and ready";

/** Toast headline: plain text, the brand styling lives in the UI around it. */
export const displayTitle = (n: Pick<AttentionNotification, "type" | "phase">) => {
  if (n.type === "input_required") return INPUT_TITLE;
  return n.phase === APP_READY_PHASE ? APP_READY_TITLE : COMPLETED_TITLE;
};

/** Toast/bell body line: "Project: message", with em/en dashes in the AI-written text turned into commas. */
export const attentionLine = (n: Pick<AttentionNotification, "projectName" | "message">) => {
  const message = n.message?.replace(/\s*[\u2013\u2014]\s*/g, ", ") ?? null;
  return [n.projectName, message].filter(Boolean).join(": ");
};

const PROJECT_ROUTES = ["/projects/:projectId", "/projects/:type/:projectId"];
const NON_PROJECT_SEGMENTS = new Set(["all"]);

/** The project whose editor is on screen at `pathname`, or null (engagement/collaborate sub-pages don't count). */
export function projectIdFromPath(pathname: string): string | null {
  for (const pattern of PROJECT_ROUTES) {
    const match = matchPath({ path: pattern, end: true }, pathname);
    const id = match?.params.projectId;
    if (id && !NON_PROJECT_SEGMENTS.has(id)) return id;
  }
  return null;
}

/** Normalise one row of the GET /notifications snapshot. */
export function fromSnapshotRow(row: Record<string, unknown>): AttentionNotification | null {
  const type = row.type;
  if ((type !== "input_required" && type !== "completed") || typeof row.eventKey !== "string" || typeof row.projectId !== "string") {
    return null;
  }
  return {
    eventKey: row.eventKey,
    projectId: row.projectId,
    projectName: (row.projectName as string | null) ?? null,
    type,
    phase: (row.phase as string | null) ?? null,
    title: (row.title as string) || (type === "input_required" ? INPUT_TITLE : COMPLETED_TITLE),
    message: (row.message as string | null) ?? null,
    occurredAt: (row.occurredAt as string | null) ?? null,
  };
}

const byNewest = (a: AttentionNotification, b: AttentionNotification) =>
  (b.occurredAt ?? "").localeCompare(a.occurredAt ?? "");

/** Keep only the newest completion per project — a later turn's success supersedes an older unread one. */
function latestCompletionPerProject(items: AttentionNotification[]): AttentionNotification[] {
  const newestCompletion = new Map<string, AttentionNotification>();
  for (const item of items) {
    if (item.type !== "completed") continue;
    const current = newestCompletion.get(item.projectId);
    if (!current || (item.occurredAt ?? "") > (current.occurredAt ?? "")) newestCompletion.set(item.projectId, item);
  }
  return items.filter((item) => item.type !== "completed" || newestCompletion.get(item.projectId) === item);
}

export function fromSnapshot(rows: unknown): AttentionNotification[] {
  if (!Array.isArray(rows)) return [];
  const seen = new Set<string>();
  const items: AttentionNotification[] = [];
  for (const row of rows) {
    const item = fromSnapshotRow((row ?? {}) as Record<string, unknown>);
    if (!item || seen.has(item.eventKey)) continue;
    seen.add(item.eventKey);
    items.push(item);
  }
  return latestCompletionPerProject(items).sort(byNewest);
}

/**
 * Apply one stream event. Resolution matches the exact eventKey, never "everything for the
 * project", so out-of-order delivery can't clear a newer request.
 */
export function applyEvent(items: AttentionNotification[], event: AttentionStreamEvent): AttentionNotification[] {
  switch (event.type) {
    case "input_resolved":
      return items.filter((item) => item.eventKey !== event.eventKey);
    case "cleared":
      return items.length ? [] : items;
    case "inputs_reset":
      return items.filter(
        (item) => !(item.type === "input_required" && item.projectId === event.projectId && !item.eventKey.startsWith(`${event.runId}:`)),
      );
    case "input_required":
    case "completed": {
      if (!event.eventKey || items.some((item) => item.eventKey === event.eventKey)) return items;
      const next = fromSnapshotRow(event as unknown as Record<string, unknown>);
      if (!next) return items;
      return latestCompletionPerProject([next, ...items]).sort(byNewest);
    }
    default:
      return items;
  }
}

export function pendingInputCount(items: AttentionNotification[]): number {
  return items.filter((item) => item.type === "input_required").length;
}

export function hasCompletion(items: AttentionNotification[]): boolean {
  return items.some((item) => item.type === "completed");
}

/** Tab-title label (no separator, no count); input outranks completion because it blocks progress. */
export function titlePrefix(items: AttentionNotification[]): string {
  if (pendingInputCount(items) > 0) return "Waiting for input";
  if (!hasCompletion(items)) return "";
  return items.some((item) => item.type === "completed" && item.phase !== APP_READY_PHASE) ? "Completed" : "App ready";
}

// Label + " · ", or the bare label (document.title trims trailing space); repeats and older counted/emoji labels strip too so stale tabs heal
const TITLE_LABEL_RE = /^(?:(?:(?:🟡 )?(?:\(\d+\) )?Waiting for input|(?:✅ )?Completed|App ready)(?:\s*·\s*|\s*$))+/;

export function stripTitleLabel(title: string): string {
  return title.replace(TITLE_LABEL_RE, "");
}

export function withTitleLabel(label: string, title: string): string {
  const base = stripTitleLabel(title);
  if (!label) return base;
  return base ? `${label} · ${base}` : label;
}
